<template>
  <div class="user-menu-wrapper">
    <Teleport to="body">
      <div v-if="isOpen" class="user-dropdown-overlay" @click="closeDropdown">
        <div class="user-dropdown" @click.stop @mouseenter="onDropdownMouseEnter" @mouseleave="onDropdownMouseLeave">
          <div class="profile-section">
            <p class="user-name">{{ user?.firstName }} {{ user?.lastName }}</p>
            <p class="user-email">{{ user?.email }}</p>
          </div>
          <hr>
          <router-link :to="configStore.isCountdownMode ? '/countdown' : '/'" class="dropdown-item home-link" @click="closeDropdown">{{ t('header.nav.home') }}</router-link>
          <router-link to="/account" class="dropdown-item" @click="closeDropdown">{{ t('header.user.my_account') }}</router-link>
          <button 
            @click="$emit('logout')" 
            :disabled="isLoggingOut"
            class="dropdown-item logout"
          >
            <span v-if="isLoggingOut" class="spinner"></span>
            {{ isLoggingOut ? t('account.personal_data.btn.logging_out') : t('header.user.logout') }}
          </button>
        </div>
      </div>
      
      <div v-if="isLoggingOut" class="logout-loading-overlay">
        <div class="loading-content">
          <div class="spinner-large"></div>
          <p>{{ t('account.personal_data.btn.logging_out') }}</p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useLanguage } from '@/composables/useLanguage'
import { useConfigStore } from '@/stores/configStore'

const { t } = useLanguage()
const configStore = useConfigStore()

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  user: {
    type: Object,
    default: null
  },
  isLoggingOut: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'logout', 'dropdown-hover', 'dropdown-leave'])

const isClickOpen = ref(false)

const closeDropdown = () => {
  isClickOpen.value = false
  emit('close')
}

const onDropdownMouseEnter = () => {
  emit('dropdown-hover')
}

const onDropdownMouseLeave = () => {
  emit('dropdown-leave')
}
</script>

<style scoped>
.user-menu-wrapper {
  display: contents;
}

.user-dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  pointer-events: none;
}

.user-dropdown {
  position: fixed;
  top: 80px;
  right: 30px;
  background: white;
  border: 1px solid #111;
  border-radius: 0;
  box-shadow: none;
  min-width: 250px;
  padding: 0;
  z-index: 10000;
  animation: slideDown 0.3s ease;
  pointer-events: auto;
}

@media (max-width: 1024px) {
  .user-dropdown {
    top: 70px;
    right: 16px;
    left: 16px;
    width: auto;
    min-width: 0;
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.profile-section {
  padding: 20px;
  background: #f9f9f9;
}

.user-name {
  font-weight: 700;
  color: #111;
  font-size: 13px;
  margin: 0 0 5px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.user-email {
  font-size: 11px;
  color: #666;
  margin: 0;
  font-weight: 400;
  word-break: break-word;
}

.user-dropdown hr {
  margin: 0;
  border: none;
  border-top: 1px solid #eee;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 15px 20px;
  border: none;
  background: none;
  text-align: left;
  color: #111;
  text-decoration: none;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.3s ease;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-sizing: border-box;
}

.dropdown-item:hover {
  background-color: #111;
  color: #fff;
}

.dropdown-item.home-link {
  color: #000;
  background-color: rgba(0, 0, 0, 0.03);
  font-weight: 800;
}

.dropdown-item.logout {
  color: #7D2121;
  border-top: 1px solid #eee;
}

.dropdown-item.logout:hover:not(:disabled) {
  background-color: #7D2121;
  color: #fff;
}

.dropdown-item.logout:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(125, 33, 33, 0.3);
  border-top: 2px solid #7D2121;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 8px;
}

.logout-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  z-index: 11000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-content {
  text-align: center;
  color: #111;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.loading-content p {
  font-size: 12px;
  font-weight: 700;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.spinner-large {
  width: 40px;
  height: 40px;
  border: 2px solid #eee;
  border-top: 2px solid #111;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
